import { AdminConsoleRow, AdminConsoleStep, OtherFeesAndDeductions, TrustInvoice, TrustPolicy } from "../features/AdminConsole/types";
import { DropdownItem, ListItem, Role, RoleAccess, RoleRestrictedFeature, UserClientData, UserOrganizationData } from "../types";
import { clone, isString, mapValues } from "lodash";

import { FilterOptions } from "@itm21st/faceted-filter";
import { durationInYears } from "@progress/kendo-date-math";

export const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

export const currencyFormatterDecimal = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

export const percentageFormatterDecimal = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const activePolicyStatuses: string[] = [
    "In Force",
    "Matured - Pending",
    "Pending - Approval",
    "Pre-Acceptance",
    "Lapsed - Pending Planned",
    "Lapsed - Pending Unplanned",
    "Surrendered - Pending",
    "Exchanged - Pending",
    "Replaced - Pending",
    "Converted - Pending",
    "Death Claim - Pending",
    "Transferred - Pending",
    "Pending Resignation",
    "Pending Removal",
    "Pending Closing",
    "Pending Approval",
    "Origination"
];

export const inactivePolicyStatuses: string[] = [
    "Matured - Confirmed",
    "Lapsed - Confirmed",
    "Surrendered - Confirmed",
    "Exchanged - Confirmed",
    "Replaced - Confirmed",
    "Converted - Confirmed",
    "Death Claim - Confirmed",
    "Transferred - Confirmed",
    "Resigned as Trustee",
    "Removed as Trustee",
    "Closed",
    "Not Accepted"
];

export const stringDays: string[] = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const stringMonths: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

export const trustStatusBuckets = [
    { name: "Active", properties: ["Active"], statusExplanation: "Trusts are currently active." },
    {
        name: "Pending Additions",
        properties: ["Pending Approval"],
        statusExplanation: "Newly opened trusts need approval to become active"
    },
    {
        name: "Pending Closing",
        properties: ["Pending Removal", "Pending Resignation", "Pending Closing"],
        statusExplanation: "Trusts are one of the following statuses: Pending Removal, Pending Resignation, Pending Closing"
    }
];

export const policyStatusBuckets = [
    { name: "In Force", properties: ["In Force"], statusExplanation: "Policies are currently active" },
    {
        name: "Pending Additions",
        properties: ["Pending Approval", "Pre-Acceptance", "Transferred - Pending"],
        statusExplanation: "Policies are one of the following statuses: Pending Approval, Pre-Acceptance, Transferred - Pending"
    },
    {
        name: "Carrier Disbursement - Pending",
        properties: ["Surrendered - Pending", "Death Claim - Pending", "Matured - Pending"],
        statusExplanation: "Policies are one of the following statuses: Surrendered - Pending, Death Claim - Pending, Matured - Pending"
    },
    {
        name: "Pending Closing",
        properties: [
            "Lapsed - Pending Planned",
            "Lapsed - Pending Unplanned",
            "Exchanged - Pending",
            "Replaced - Pending",
            "Converted - Pending",
            "Pending Resignation",
            "Pending Removal",
            "Pending Closing"
        ],
        statusExplanation:
            "Policies are one of the following statuses: Lapsed - Pending Planned, Lapsed - Pending Unplanned, Exchanged - Pending, Replaced - Pending, Converted - Pending, Pending Resignation, Pending Removal, Pending Closing"
    }
];
export const activeTrustStatuses = ["Active", "Pending Approval", "Pending Resignation", "Pending Removal", "Pending Closing"];

export const inactiveTrustStatuses = [
    "Resigned as Trustee",
    "Removed as Trustee",
    "Closed",
    "Not Accepted",
    "Surrendered - Confirmed",
    "Lapsed - Confirmed",
    "Matured - Confirmed"
];

export const createStringDate = (dateToConvert: Date): string => {
    const month = stringMonths[dateToConvert.getMonth()];
    const date = dateToConvert.getDate();
    const day = stringDays[dateToConvert.getDay()];
    const year = dateToConvert.getFullYear();
    return `${day}, ${month} ${date}, ${year}`;
};

export const dateDiff = (date1: Date, date2: Date): number => {
    const month1 = date1.getMonth();
    const day1 = date1.getDate();
    const month2 = date2.getMonth();
    const day2 = date2.getDate();

    let years = durationInYears(date1, date2);
    if (month1 > month2 || (month1 === month2 && day1 > day2)) {
        years -= 1;
    }
    return years;
};

// support string and Date inputs
export const dateFormatter = (ufDate: string | Date): string => {
    if (!ufDate) return "";
    const iso = ufDate instanceof Date ? toISODateString(ufDate) : ufDate;
    return `${iso.substring(5, 7)}/${iso.substring(8, 10)}/${iso.substring(0, 4)}`;
};

export const formatAuditDate = (input: string): string => {
    if (!input) return "";
    const trimmed = input.endsWith("Z") ? input.slice(0, -1) : input;
    return new Date(trimmed)
        .toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true
        })
        .replace(/, /g, " ");
};

export const timeFormatter = (ufDate: string): string => {
    let time = ufDate.substring(11, 19);
    let meridiem: "AM" | "PM";
    let hours;
    if (parseInt(time.substring(0, 2), 16) < 12) {
        meridiem = "AM";
    } else {
        hours = +time.substring(0, 2) - 12;
        time = hours.toString() + time.slice(2);
        meridiem = "PM";
    }

    return `${time} ${meridiem}`;
};

export const toISODateString = (date: Date): string => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${year}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`;
};

export const getIsoFormDate = (date: string | null): string => {
    if (!date) {
        return "";
    }
    return date.split("T")[0];
};

export const isValidDateString = (date: string): boolean => {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return false;

    const [yearStr, monthStr, dayStr] = date.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);

    if (year < 1900 || year > 2100) return false;
    if (month < 1 || month > 12) return false;

    // Check day against actual days in month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;

    return true;
};

export function fromIsoDateString(date: string): Date {
    const [year, month, day] = date.split("-");
    return new Date(+year, +month - 1, +day);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const reducer = (accumulator: any, currentValue: any): any => accumulator + currentValue;

export const boolToYesNo = (value: boolean | null): "Yes" | "No" => (value ? "Yes" : "No");
export const boolToYesNoNP = (value: boolean | null): string => {
    switch (value) {
        case true:
            return "Yes";
        case false:
            return "No";
        default:
            return "N/P";
    }
};

export const maskSSN = (SSN: string, maskCharacter?: string): string | null => {
    if (!SSN) {
        return null;
    }
    const lastFour = SSN.substring(SSN.length - 4);
    const localMaskChar = maskCharacter || "X";
    return `${[localMaskChar, localMaskChar, localMaskChar].join("")}-${[localMaskChar, localMaskChar].join("")}-${lastFour}`;
};

// type options are 'long' (January), 'narrow' (J), 'short' (Jan), '2-digit' (01)
export const getMonths = (type: "long" | "narrow" | "short" | "2-digit"): string[] =>
    Array.from({ length: 12 }, (x, index) => new Date(0, index).toLocaleDateString("en-US", { month: type }));

export const addDaysToDate = (date: Date, n: number): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
};

export const yesNoNpFormatter = (value: number | null): string => {
    switch (value) {
        case 1:
            return "Yes";
        case 2:
            return "No";
        default:
            return "N/P";
    }
};

export const yesNoBooleanOptions = (): DropdownItem[] => {
    return [
        {
            text: "Yes",
            key: "Yes",
            value: true
        },
        {
            text: "No",
            key: "No",
            value: false
        }
    ];
};

export const yesNoNpOptions = (): DropdownItem[] => {
    return [
        {
            text: "Yes",
            key: "Yes",
            value: 1
        },
        {
            text: "No",
            key: "No",
            value: 2
        },
        {
            text: "N/P",
            key: "N/P",
            value: 3
        }
    ];
};

export const yesNpNaFormatter = (value: number): string => {
    switch (value) {
        case 1:
            return "Yes";
        case 2:
            return "N/P";
        default:
            return "N/A";
    }
};

export const filterSelector = (
    dataArray: any[],
    relationshipManagerContactID?: number,
    clientID?: number,
    trustAdvisorContactID?: number,
    administratorContactID?: number,
    clientAdvisorContactID?: number
): any[] => {
    let filteredArray = dataArray;
    if (relationshipManagerContactID) {
        filteredArray = filteredArray.filter((item) => item.RelationshipManagerContactID === relationshipManagerContactID);
    }
    if (trustAdvisorContactID) {
        filteredArray = filteredArray.filter((item) => item.TrustAdvisorContactID === trustAdvisorContactID);
    }
    if (administratorContactID) {
        filteredArray = filteredArray.filter((item) => item.AdministratorContactID === administratorContactID);
    }
    if (clientAdvisorContactID) {
        filteredArray = filteredArray.filter((item) => item.ClientAdvisorContactID === clientAdvisorContactID);
    }
    if (clientID) {
        filteredArray = filteredArray.filter((item) => item.ClientID === clientID);
    }
    return filteredArray;
};

export const convertBufferToBase64 = (buffer: Buffer): string => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

export const notImplemented = () => {
    throw new Error(`Not implemented!`);
};

export const kendoDateFormat = "{0: MM/dd/yyyy}";
export const kendoDateTimeFormat = "{0: M/d/yyyy, h:mm a}";
export const kendoCurrencyFormat = "{0:C}";
export const kendoPageSizes = { pageSizes: [25, 50, 100] };

/**
 * Mutates an array of objects by parsing the selected fields as Date objects
 * @param {Object[]} data - An array of objects
 * @param {string[]} dateFields - An array of field names to be parsed as Date objects
 */
export const parseDates = (data: any[], fields: string[]): any[] => {
    const parsed = data.map((row) => {
        const mutableRow = { ...row };
        fields.forEach((field) => {
            if (mutableRow[field]) {
                const dateWithoutZ = row[field].slice(0, -1);
                mutableRow[field] = new Date(dateWithoutZ);
            }
        });
        return mutableRow;
    });
    return parsed;
};

export const checkPremiumDueDate = (range: string, nextDueDate: string | Date): boolean => {
    let inRange = false;
    const currentDate = new Date();
    const thirtyDaysOut = new Date(clone(currentDate).setDate(currentDate.getDate() + 30));
    const myDate = new Date(nextDueDate);

    switch (range) {
        case "Past Due Premiums":
            inRange = myDate <= currentDate;
            break;
        case "Premiums Due 1-30 Days":
            inRange = myDate > currentDate && myDate <= thirtyDaysOut;
            break;
        default:
            inRange = false;
    }
    return inRange;
};

export const replaceSpecialCharacters = (stringToCheck: string): string =>
    stringToCheck
        .replace(/&amp;apos;/gi, "'")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, `"`)
        .replace(/&apos;/gi, `'`)
        .replace(/&amp;/gi, "&")
        .replace(/&rsquo;/gi, "'")
        .replace(/&ndash;/gi, "--");

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const cleanDataObj = (data: any) =>
    mapValues(data, (value) => {
        if (!isString(value)) {
            return value;
        }
        return replaceSpecialCharacters(value);
    });

export const cleanDataArray = <T>(dataArr: T[] | undefined): T[] => {
    if (!dataArr) {
        return [];
    }
    return dataArr.map((dataItem) => cleanDataObj(dataItem));
};

export const getPortfolioListOptions = (appdata: {
    organization: UserOrganizationData;
    clients: UserClientData[];
    role: Role;
}): DropdownItem[] => {
    const options: (DropdownItem | undefined)[] = appdata.clients.map((c) => {
        if (
            (appdata.organization.HasCloselyHeldBusiness && c.HasCloselyHeldBusiness) ||
            appdata.organization.UserHasTrustServices ||
            appdata.organization.UserHasWillFile
        ) {
            return {
                key: c.ClientID,
                text: `${c.CommonName}${c.IsInnovestPortfolio ? "*" : ""}`,
                value: c.ClientID
            };
        }
        return undefined;
    });

    return cleanDataArray(options.filter((option): option is DropdownItem => option !== undefined));
};

export const thirtyMinuteTimeout = 1800000;

export const isTrustManaged = (clientID: number, clientsOnUser: any[]): boolean => {
    const client = clientsOnUser.find((c) => c.ClientID === clientID);
    return client ? client.IsManaged : false;
};

export const isTrustPolicyManaged = (clientID: number, clientsOnUser: any[]): boolean => {
    const client = clientsOnUser.find((c) => c.ClientID === clientID);
    return client ? client.IsManaged : false;
};

export const buildSettingsObject = (settingsArray: any[]): any => {
    const settingsObject: any = {};

    settingsArray.forEach((setting: any) => {
        if (setting.SettingValue) {
            if (setting.SettingName === "AdminFeeDueDate") {
                const pipeIndex: number = setting.SettingValue.indexOf("|");
                if (pipeIndex > -1) {
                    settingsObject.AdminFeeDueDateOption = parseInt(setting.SettingValue.substring(0, pipeIndex));
                    settingsObject.AdminFeeDueDateValue = setting.SettingValue.substring(pipeIndex + 1);
                } else {
                    settingsObject.AdminFeeDueDateOption = parseInt(setting.SettingValue);
                    settingsObject.AdminFeeDueDateValue = "";
                }
            } else {
                const intValue: number | null = parseInt(setting.SettingValue);
                if (isFinite(intValue)) {
                    settingsObject[setting.SettingName] = intValue;
                } else {
                    if (setting.SettingName.includes("mergefield")) {
                        settingsObject[setting.SettingName] = setting.SettingValue;
                    } else {
                        settingsObject[setting.SettingName] = setting.SettingValue.toLowerCase();
                    }
                }
            }
        }
    });
    return settingsObject;
};

export const letterSignatoryTypes: DropdownItem[] = [
    { text: "Administrator", value: 1, key: 1 },
    { text: "TRM", value: 2, key: 2 },
    { text: "TA", value: 3, key: 3 },
    { text: "CA", value: 4, key: 4 },
    { text: "Trustee", value: 5, key: 5 }
];

export const trustTypeOptions: DropdownItem[] = [
    { text: "Trust", value: "trust", key: "trust" },
    { text: "Non-Trust", value: "nontrust", key: "nontrust" }
];

export const yesNoOptions: DropdownItem[] = [
    { text: "Yes", value: "yes", key: "yes" },
    { text: "No", value: "no", key: "no" }
];

export const noDataMsg = "No Data Available";

export const formatRemainderPaidBy = (options: ListItem[], remainderPaidBy: string | null): string | null => {
    // Data allows for multiple values, and can contain either string values ('Automatic Premium Loan, Dividiends Reduce') that are comma delimited,
    // or ListItemID numbers ('317|319|') that are delimited by pipes.
    if (remainderPaidBy) {
        if (isNaN(parseInt(remainderPaidBy.substring(0)))) {
            return remainderPaidBy;
        }
        const remainders = remainderPaidBy.split("|");
        const textOptions = options
            .filter((o: ListItem) => remainders.find((r: string) => o.ListItemID === parseInt(r)))
            .map((r: any) => r.ItemText);
        return textOptions.join(", ");
    }
    return null;
};

export const firstLetterUppercase = (input: string): string => {
    return input.charAt(0).toUpperCase() + input.slice(1);
};

export const scrubHtml = (html: string): string => {
    const scrubbed: string = replaceSpecialCharacters(html.replace(/<[^>]*>/g, " ").trim());
    return scrubbed;
};

export const batchDownloadLimit = 100;

export type PremiumMode = "Annual" | "Quarterly" | "Semi-Annual" | "Monthly" | "Single Premium";

export const calculateAnnualizedPremium = (mode: PremiumMode, value: number): number => {
    switch (mode) {
        case "Annual":
            return value * 1 || 0;
        case "Quarterly":
            return value * 4 || 0;
        case "Semi-Annual":
            return value * 2 || 0;
        case "Monthly":
            return value * 12 || 0;
        case "Single Premium":
            return value || 0;
        default:
            return value * 1;
    }
};

export const formatDateForFilename = (): string => {
    const today = new Date().toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${today.substring(6)}${today.substring(0, 2)}${today.substring(3, 5)}`;
};

export const sectionSplitter = "<!--SECTION SPLIT-->";

export const dataTypeOptions = [
    { text: "Text", value: 1 },
    { text: "Percentage", value: 2 },
    { text: "Currency", value: 3 },
    { text: "Date", value: 4 }
];

export const roleAccess: RoleAccess = {
    "Super Admin": [],
    Administrator: ["Organization"],
    "Role Admin": [
        "Portfolio",
        "Branch",
        "TrustEdit",
        "PolicyEdit",
        "Reports",
        "TrustOnboard",
        "AdminConsole",
        "CloselyHeldBusiness",
        "CloselyHeldBusinesses",
        "WillEdit",
        "TrustAgreementReview",
        "WorkflowTemplateEdit"
    ],
    "Trust Advisor": [
        "Organization",
        "Portfolio",
        "Branch",
        "TrustEdit",
        "PolicyEdit",
        "TrustOnboard",
        "AdminConsole",
        "WillEdit",
        "TrustAgreementReview",
        "WorkflowTemplateEdit"
    ]
};

export const roleCanAccess = (page: RoleRestrictedFeature, role: Role | undefined): boolean => {
    return !!role && !roleAccess[role].includes(page);
};

export const redact = (stringToRedact: string, charactersToKeep: number): string => {
    const redacted: any = [];
    const hyphens: any = [];
    const numArray: any = [];
    stringToRedact.split("").forEach((number, index) => {
        if (/[-/,.()&'":;]/.test(number)) {
            hyphens.push({ symbol: number, index });
        } else {
            numArray.push(number);
        }
    });
    numArray.forEach((number: string, index: number) => {
        if (index >= numArray.length - charactersToKeep) {
            redacted.push(number);
        } else {
            number = "x";
            redacted.unshift(number);
        }
    });
    hyphens.forEach((hyphen: any) => {
        redacted.splice(hyphen.index, 0, hyphen.symbol);
    });
    return redacted.join("");
};

export const calculateNextPremiumDueDate = (currentPremiumDueDate: Date | string, premiumMode: string): string | null => {
    if (!premiumMode || !currentPremiumDueDate) {
        return null;
    }
    let nextPremiumDueDate: any = new Date(currentPremiumDueDate);

    switch (premiumMode) {
        case "Annual":
            nextPremiumDueDate = nextPremiumDueDate.setFullYear(nextPremiumDueDate.getFullYear() + 1);
            break;
        case "Monthly":
            nextPremiumDueDate = nextPremiumDueDate.setMonth(nextPremiumDueDate.getMonth() + 1);
            break;
        case "Quarterly":
            nextPremiumDueDate = nextPremiumDueDate.setMonth(nextPremiumDueDate.getMonth() + 3);
            break;
        case "Semi-Annual":
            nextPremiumDueDate = nextPremiumDueDate.setMonth(nextPremiumDueDate.getMonth() + 6);
            break;
        case "Single Premium":
            nextPremiumDueDate = "";
            break;
        default:
            nextPremiumDueDate = "";
            break;
    }
    if (nextPremiumDueDate) {
        return dateFormatter(new Date(nextPremiumDueDate).toISOString());
    }
    return nextPremiumDueDate;
};

export const emdash = `—`;

/**
 * Common contact filter options for FacetedFilter
 * Used across report files to filter by TRM, Trust Administrator, Trust Advisor, and Client Advisor.
 */
export const contactFilterOptions: FilterOptions[] = [
    {
        property: "TRM",
        label: "TRM",
        facetOptions: { null: { label: "None" } },
        defaultCollapsed: true
    },
    {
        property: "TrustAdministrator",
        label: "Trust Administrator",
        facetOptions: { null: { label: "None" } },
        defaultCollapsed: true
    },
    {
        property: "TrustAdvisor",
        label: "Trust Advisor",
        facetOptions: { null: { label: "None" } },
        defaultCollapsed: true
    },
    {
        property: "ClientAdvisor",
        label: "Client Advisor",
        facetOptions: { null: { label: "None" } },
        defaultCollapsed: true
    }
];

export const calculateTotalAmountRequested = (
    trustPolicies: TrustPolicy[],
    trustInvoices: TrustInvoice[],
    otherFees: OtherFeesAndDeductions,
    deductionsOrPayments: OtherFeesAndDeductions,
    fromWaitingForGift?: boolean
): number => {
    let total = 0;
    const includedPolicies = trustPolicies.filter(({ Include }) => Include);
    const includedFees = trustInvoices.filter(({ Include }) => Include);

    const totalIncludedPremiums =
        (includedPolicies.length &&
            includedPolicies
                .map(
                    (tp) =>
                        parseFloat(tp.AmountPayable.toString()) +
                        parseFloat(tp.LoanInterest?.toString() || "0") +
                        parseFloat(tp.LoanPrincipal?.toString() || "0")
                )
                .reduce(reducer)) ||
        0;

    const totalIncludedFees = (includedFees.length && includedFees.map((fee) => parseFloat(fee.Amount.toString())).reduce(reducer)) || 0;
    total = totalIncludedPremiums + totalIncludedFees;
    if (otherFees.amount) {
        total += parseFloat(otherFees.amount as string);
    }
    if (deductionsOrPayments.amount) {
        if (fromWaitingForGift) {
            total += parseFloat(deductionsOrPayments.amount as string);
        } else {
            total -= parseFloat(deductionsOrPayments.amount as string);
        }
    }

    const totalAmountRequested = Math.round(total * 100) / 100;

    return totalAmountRequested;
};

export const getNextAdminConsoleStep = (data: AdminConsoleRow, proposedNextStep: string, isPremium: boolean): AdminConsoleStep | string => {
    // This function allows us to skip a status based on settings
    const possiblePremiumSkippedStepsList = [
        {
            stepNum: 0,
            step: "Waiting for Gift",
            status: "Waiting for Gift/ Fee",
            setting: "GiftsAutoReceived",
            value: "yes",
            listItemID: 1151
        },
        { stepNum: 1, step: "Crummey Letters", status: "Crummey Letters", setting: "CrummeysRequired", value: "no", listItemID: 1148 },
        { stepNum: 2, step: "Premium Payments", status: "Premium/ Fee Payments", listItemID: 1150 }
    ];
    const possibleFeeSkippedStepsList = [
        {
            stepNum: 0,
            step: "Waiting for Fee",
            status: "Waiting for Gift/ Fee",
            setting: "FeeAutoReceived",
            value: "yes",
            listItemID: 1159
        },
        { stepNum: 1, step: "Crummey Letters", status: "Crummey Letters", setting: "FeeSubjectToCrummey", value: "no", listItemID: 1156 },
        { stepNum: 2, step: "Fee Payments", status: "Premium/ Fee Payments", listItemID: 1158 }
    ];

    const arrayToUse = isPremium ? possiblePremiumSkippedStepsList : possibleFeeSkippedStepsList;
    const step = arrayToUse.find((a) => a.step === proposedNextStep);

    if (step && data && step.value && (data[step.setting] === step.value || !data[step.setting])) {
        const nextStep = arrayToUse.find((a) => a.stepNum === step.stepNum + 1);
        if (nextStep) {
            return nextStep as AdminConsoleStep;
        }
    }

    return proposedNextStep;
};

export const getUpdatedValues = <T extends Record<string, any>>(original: T, modified: T): Partial<T> => {
    const updates: Partial<T> = {};
    for (const [key, value] of Object.entries(modified)) {
        if (original[key as keyof T] !== value) {
            updates[key as keyof T] = value as T[keyof T];
        }
    }
    return updates;
};
